import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { StatusBadge } from "@/pages/dashboard/_shared";
import {
  Activity, Clock, ShieldCheck, Truck, FileText, ChevronDown, ChevronRight,
} from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
}

function Section({ id, icon, title, count, open, onToggle, children }) {
  return (
    <div className="border border-zinc-200" data-testid={`job-activity-section-${id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-3 py-2.5 bg-zinc-50 hover:bg-zinc-100 transition-colors text-left"
        data-testid={`job-activity-toggle-${id}`}
      >
        {open ? <ChevronDown className="w-3.5 h-3.5 text-zinc-500" /> : <ChevronRight className="w-3.5 h-3.5 text-zinc-500" />}
        {icon}
        <span className="text-xs font-bold uppercase tracking-widest">{title}</span>
        <span className="ml-auto text-[10px] font-mono text-zinc-500">{count}</span>
      </button>
      {open && <div className="p-3">{children}</div>}
    </div>
  );
}

function EmptyRow({ label }) {
  return <div className="text-xs text-zinc-400 italic">{label}</div>;
}

export function JobActivity({ jobId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({ timesheets: true });

  useEffect(() => {
    api.get(`/jobs/${jobId}/activity`).then((r) => setData(r.data)).catch(() => {});
  }, [jobId]);

  if (!data) return null;
  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));

  const ts = data.timesheets || { items: [], count: 0, total_hours: 0 };
  const safetyCount = (data.qa_records?.length || 0) + (data.inspections?.length || 0)
    + (data.safety_forms?.length || 0) + (data.sssps?.length || 0) + (data.daily_logs?.length || 0);

  return (
    <section className="hard-card p-5" data-testid="job-activity-panel">
      <div className="label-tag mb-3 flex items-center gap-1.5">
        <Activity className="w-3 h-3" /> Job activity
      </div>
      <div className="space-y-2">
        {/* TIMESHEETS */}
        <Section id="timesheets" icon={<Clock className="w-3.5 h-3.5 text-[#FF5F15]" />}
                 title={data.is_office_view ? "Timesheets" : "My timesheets"}
                 count={`${ts.count} · ${ts.total_hours}h`}
                 open={!!open.timesheets} onToggle={() => toggle("timesheets")}>
          {ts.items.length === 0 ? <EmptyRow label="No timesheets logged against this job yet." /> : (
            <div className="max-h-72 overflow-y-auto -mx-1">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[9px] font-bold uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
                    <th className="py-1.5 px-1">Date</th>
                    <th className="py-1.5 px-1">Worker</th>
                    <th className="py-1.5 px-1 text-right">Hours</th>
                    <th className="py-1.5 px-1 text-right">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {ts.items.map((t) => (
                    <tr key={t.id} className="border-b border-zinc-100" data-testid={`job-activity-ts-${t.id}`}>
                      <td className="py-1.5 px-1 font-mono">{t.date}</td>
                      <td className="py-1.5 px-1">{t.user_name || t.created_by_name || "—"}</td>
                      <td className="py-1.5 px-1 text-right font-mono font-bold">{t.hours}</td>
                      <td className="py-1.5 px-1 text-right"><StatusBadge status={t.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-xs text-zinc-500 mt-2">Showing latest {ts.items.length} of {ts.count}. Hours cover all matching Job timesheets.</p>
          <Link to={`/app/timesheets?job_id=${encodeURIComponent(jobId)}`} className="inline-block mt-2 text-[10px] font-bold uppercase tracking-widest text-[#FF5F15] hover:text-[#E65100]">
            View all activity →
          </Link>
        </Section>

        {/* SAFETY & QUALITY */}
        {data.site_audit_events?.length > 0 && <div className="rounded-lg border p-4 space-y-3">
          <h3 className="font-semibold text-sm">Site Audit Pass history</h3>
          {data.site_audit_events.map(event => <div key={event.id} className="text-sm"><p>{event.summary}</p><p className="text-xs text-muted-foreground">{event.actor_name ? `${event.actor_name} · ` : ''}{fmtDate(event.created_at)}</p></div>)}
        </div>}
        <Section id="safety" icon={<ShieldCheck className="w-3.5 h-3.5 text-green-700" />}
                 title="Safety & quality paperwork" count={safetyCount}
                 open={!!open.safety} onToggle={() => toggle("safety")}>
          {safetyCount === 0 ? <EmptyRow label="No safety or QA paperwork linked to this job yet." /> : (
            <div className="space-y-3">
              {data.sssps?.length > 0 && (
                <PaperList label="SSSPs" items={data.sssps.map((s) => ({
                  id: s.id, to: `/app/sssps/${s.id}`, title: s.name,
                  meta: `${s.created_by_name || ""} · ${fmtDate(s.created_at)}`, status: s.status,
                }))} testPrefix="sssp" />
              )}
              {data.qa_records?.length > 0 && (
                <PaperList label="QA records" items={data.qa_records.map((q) => ({
                  id: q.id, to: "/app/qa", title: q.template_name || q.task_type || "QA record",
                  meta: `${q.user_name || ""} · ${fmtDate(q.created_at)}`, status: q.status,
                }))} testPrefix="qa" />
              )}
              {data.inspections?.length > 0 && (
                <PaperList label="Site inspections" items={data.inspections.map((i) => ({
                  id: i.id, to: "/app/inspections", title: i.template_name || i.inspection_type || "Inspection",
                  meta: `${i.user_name || ""} · ${fmtDate(i.created_at)}`, status: i.status,
                }))} testPrefix="inspection" />
              )}
              {data.safety_forms?.length > 0 && (
                <PaperList label="Safety forms" items={data.safety_forms.map((f) => ({
                  id: f.id, to: "/app/safety", title: (f.form_type || "form").replace(/_/g, " "),
                  meta: `${f.user_name || ""} · ${fmtDate(f.created_at)}${f.severity ? ` · ${f.severity}` : ""}`, status: f.status,
                }))} testPrefix="safety-form" />
              )}
              {data.daily_logs?.length > 0 && (
                <PaperList label="Daily site logs" items={data.daily_logs.map((d) => ({
                  id: d.id, to: "/app/pm", title: `Site log — ${d.date}`,
                  meta: `${d.created_by_name || ""}${d.weather ? ` · ${d.weather}` : ""}`,
                  status: d.site_open === false ? "closed" : null,
                }))} testPrefix="daily-log" />
              )}
            </div>
          )}
        </Section>

        {/* DISPATCH HISTORY */}
        <Section id="dispatch" icon={<Truck className="w-3.5 h-3.5 text-zinc-700" />}
                 title="Dispatch history" count={data.dispatch_history?.length || 0}
                 open={!!open.dispatch} onToggle={() => toggle("dispatch")}>
          {(data.dispatch_history || []).length === 0 ? <EmptyRow label="No crew has been dispatched to this job yet." /> : (
            <div className="max-h-64 overflow-y-auto space-y-1.5">
              {data.dispatch_history.map((d) => (
                <div key={d.date} className="flex items-start gap-2 text-xs" data-testid={`job-activity-dispatch-${d.date}`}>
                  <span className="font-mono font-bold shrink-0 w-24">{d.date}</span>
                  <span className="text-zinc-600 break-words">{d.workers.join(", ")}</span>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* COMMERCIAL (office only — backend omits for field) */}
        {data.is_office_view && (
          <Section id="commercial" icon={<FileText className="w-3.5 h-3.5 text-blue-700" />}
                   title="Quotes, invoices & variations"
                   count={(data.quotes?.length || 0) + (data.invoices?.length || 0) + (data.variations?.length || 0)}
                   open={!!open.commercial} onToggle={() => toggle("commercial")}>
            <div className="space-y-3">
              {data.quotes?.length > 0 && (
                <PaperList label="Quotes" items={data.quotes.map((q) => ({
                  id: q.id, to: "/app/quotes", title: `${q.quote_number || "Quote"} · $${Number(q.total || 0).toLocaleString()}`,
                  meta: `${q.client_name || ""} · ${q.issue_date || ""}`, status: q.status,
                }))} testPrefix="quote" />
              )}
              {data.invoices?.length > 0 && (
                <PaperList label="Invoices" items={data.invoices.map((i) => ({
                  id: i.id, to: "/app/customer-invoices", title: `${i.invoice_number || "Invoice"} · $${Number(i.total || 0).toLocaleString()}`,
                  meta: `${i.client_name || ""} · ${i.issue_date || ""}`, status: i.status,
                }))} testPrefix="invoice" />
              )}
              {data.variations?.length > 0 && (
                <PaperList label="Variations" items={data.variations.map((v) => ({
                  id: v.id, to: "/app/variations", title: `${v.variation_number || "VO"} — ${(v.scope || "").slice(0, 60)}`,
                  meta: `${v.requested_by_name || ""} · ${fmtDate(v.created_at)}${v.price != null ? ` · $${Number(v.price).toLocaleString()}` : ""}`,
                  status: v.status,
                }))} testPrefix="variation" />
              )}
              {!(data.quotes?.length || data.invoices?.length || data.variations?.length) && (
                <EmptyRow label="No quotes, invoices or variations for this job." />
              )}
            </div>
          </Section>
        )}
      </div>
    </section>
  );
}

function PaperList({ label, items, testPrefix }) {
  return (
    <div>
      <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
      <div className="space-y-1">
        {items.map((it) => (
          <Link key={it.id} to={it.to}
                className="flex items-center gap-2 text-xs p-1.5 border border-zinc-100 hover:border-[#FF5F15] transition-colors"
                data-testid={`job-activity-${testPrefix}-${it.id}`}>
            <span className="font-medium truncate flex-1 capitalize">{it.title}</span>
            <span className="text-[10px] font-mono text-zinc-400 truncate hidden sm:inline">{it.meta}</span>
            {it.status && <StatusBadge status={it.status} />}
          </Link>
        ))}
      </div>
    </div>
  );
}
